import type { ClientUiImageModel, ClientUiImageSizeOption, ClientUiPublic } from '../api/platformConfig'
import { AI_PLATFORM_NOWCODING, requireCatalogPlatformId } from '../constants/aiPlatforms'

/**
 * 在已知 clientUi 与 modelId、platformId 下解析单条模型配置。
 * 多平台共用同一 modelId 时，必须存在与 platformId 一致的条目；否则返回 undefined（不做静默选首条）。
 */
export function resolveClientUiImageModel(
  clientUi: ClientUiPublic,
  modelId: string,
  platformId: string,
): ClientUiImageModel | undefined {
  const matches = clientUi.imageModels.filter((m) => m.id === modelId)
  if (matches.length === 0) return undefined
  if (matches.length === 1) return matches[0]
  return matches.find((m) => m.platform === platformId)
}

/** 当前模型专用的尺寸选项；仅当 dimensionMode="size" 时返回，否则空数组 */
export function effectiveImageSizes(
  clientUi: ClientUiPublic,
  modelId: string,
  platformId: string,
): ClientUiImageSizeOption[] {
  const m = resolveClientUiImageModel(clientUi, modelId, platformId)
  if (m?.dimensionMode === 'aspect_ratio') return []
  if (m?.sizeOptions != null) return m.sizeOptions
  return clientUi.imageSizeOptions
}

/** 当前模型宽高比选项；仅当 dimensionMode="aspect_ratio" 时返回，否则 null */
export function effectiveAspectRatios(clientUi: ClientUiPublic, modelId: string, platformId: string) {
  const m = resolveClientUiImageModel(clientUi, modelId, platformId)
  if (m?.dimensionMode === 'size') return null
  return m?.aspectRatioOptions?.length ? m.aspectRatioOptions : null
}

function dimensionsFromSizeOptions(
  opts: readonly ClientUiImageSizeOption[],
  sizeValue: string,
): { w: number; h: number } {
  const hit = opts.find((o) => o.value === sizeValue)
  if (hit) return { w: hit.previewWidth, h: hit.previewHeight }
  const first = opts[0]
  if (first) return { w: first.previewWidth, h: first.previewHeight }
  throw new Error('clientUi: empty image size option list')
}

/** 按模型解析预览宽高（须已选择有效 sizeValue） */
export function previewDimensionsFromClientUiModel(
  clientUi: ClientUiPublic,
  modelId: string,
  sizeValue: string,
  platformId: string,
): { w: number; h: number } {
  const opts = effectiveImageSizes(clientUi, modelId, platformId)
  if (opts.length === 0) {
    return { w: 1024, h: 1024 }
  }
  return dimensionsFromSizeOptions(opts, sizeValue)
}

/** 仅在全局尺寸列表上解析（无模型上下文时使用） */
export function previewDimensionsFromClientUi(clientUi: ClientUiPublic, sizeValue: string): { w: number; h: number } {
  return dimensionsFromSizeOptions(clientUi.imageSizeOptions, sizeValue)
}

export function isClientUiImageSelectionValid(
  clientUi: ClientUiPublic,
  modelId: string,
  platformId: string,
): boolean {
  return resolveClientUiImageModel(clientUi, modelId, platformId) !== undefined
}

/** 从本地偏好或清单推导初始平台；清单为空时返回空串 */
export function resolveInitialCatalogPlatformId(
  clientUi: ClientUiPublic,
  preferredModelId?: string,
): string {
  const dm = preferredModelId?.trim()
  if (dm) {
    const firstForPreferred = clientUi.imageModels.find((m) => m.id === dm)
    if (firstForPreferred) {
      return requireCatalogPlatformId(firstForPreferred.platform, 'preferredModelId')
    }
  }
  const head = clientUi.imageModels[0]
  if (head) {
    return requireCatalogPlatformId(head.platform, 'imageModels[0]')
  }
  return ''
}

function maxFromExtraNOption(model: ClientUiImageModel): number | null {
  const nOpt = model.extraOptions?.find((o) => o.key === 'n')
  if (!nOpt?.options?.length) return null
  const values = nOpt.options
    .map((o) => parseInt(String(o.value), 10))
    .filter((n) => Number.isFinite(n) && n >= 1)
  if (values.length === 0) return null
  return Math.max(...values)
}

/**
 * 在模型可用尺寸列表中保留当前值，无效或为空时取首项
 */
export function resolveApiSizeForOptions(
  sizes: readonly ClientUiImageSizeOption[],
  current: string,
): string {
  if (sizes.length === 0) return ''
  const trimmed = current.trim()
  if (trimmed && sizes.some((s) => s.value === trimmed)) return trimmed
  return sizes[0]!.value
}

/**
 * 模型有 aspect_ratio 选项时校验/回填；无选项时保持原值。
 *
 * @param aspectOptions 当前模型可选比例
 * @param current       用户当前选择
 * @param fallback      当前为空时的默认比例
 * @param keepUnknown   为 true 时保留不在选项集合内的既有选择（创作页使用：
 *                      用户选了「其他比例」后不能被静默跳回横屏；非法值交给上游显式报错）
 */
export function resolveAspectRatioForOptions(
  aspectOptions: readonly { value: string }[] | null | undefined,
  current: string,
  fallback = '16:9',
  keepUnknown = false,
): string {
  const trimmed = current.trim()
  if (keepUnknown && trimmed) return trimmed
  if (!aspectOptions?.length) return trimmed || fallback
  const ok = new Set(aspectOptions.map((o) => o.value))
  if (trimmed && ok.has(trimmed)) return trimmed
  const preferred = aspectOptions.find((o) => o.value === fallback)
  return preferred?.value ?? aspectOptions[0]!.value
}

/** 平台下模型无效或为空时选该平台首条模型 */
export function resolveModelIdForPlatform(
  models: readonly ClientUiImageModel[],
  platformId: string,
  currentModelId: string,
): string {
  const pid = platformId.trim()
  if (!pid) return ''
  const platformModels = models.filter((m) => m.platform === pid)
  if (platformModels.length === 0) return ''
  const mid = currentModelId.trim()
  if (mid && platformModels.some((m) => m.id === mid)) return mid
  return platformModels[0]!.id
}

/** 切换模型时同步高级选项：保留仍合法的值，其余用 defaultValue */
export function syncExtraParamValuesForModel(
  model: ClientUiImageModel | undefined,
  prev: Record<string, string>,
): Record<string, string> {
  const opts = model?.extraOptions
  if (!opts?.length) return {}
  const next: Record<string, string> = {}
  for (const opt of opts) {
    const prevValue = prev[opt.key]
    const validValues = new Set((opt.options ?? []).map((o) => o.value))
    if (prevValue != null && validValues.has(prevValue)) {
      next[opt.key] = prevValue
    } else if (opt.defaultValue != null) {
      next[opt.key] = opt.defaultValue
    }
  }
  return next
}

/**
 * 模型单次请求最多可生成的图片张数（创作页「生成数量」与请求 n 的上限）。
 * 优先读 extraOptions.n 的可选值；否则读 nMax；NowCoding 固定为 1。
 * 上限跟随后端模型定义（QIQI gpt-image-2 已放开到 10），不再硬编码为 4。
 */
export function maxImagesPerRequestForModel(
  clientUi: ClientUiPublic,
  modelId: string,
  platformId: string,
): number {
  const pid = requireCatalogPlatformId(platformId, 'maxImagesPerRequestForModel')
  if (pid === AI_PLATFORM_NOWCODING) return 1

  const m = resolveClientUiImageModel(clientUi, modelId, platformId)
  if (!m) return 1

  const fromExtra = maxFromExtraNOption(m)
  const fromNMax = typeof m.nMax === 'number' && m.nMax >= 1 ? Math.floor(m.nMax) : null

  let cap = 1
  if (fromExtra != null && fromNMax != null) {
    cap = Math.min(fromExtra, fromNMax)
  } else if (fromExtra != null) {
    cap = fromExtra
  } else if (fromNMax != null) {
    cap = fromNMax
  }

  return Math.min(10, Math.max(1, cap))
}
